import React, { useEffect, useState } from 'react';
import { useFinanceiro } from 'features/financeiro/hooks/useFinanceiro';
import { useFinanceiroData } from 'features/financeiro/hooks/useFinanceiroData';
import { LoadingSpinner, CompanyChangeBlur } from 'components/common';
import PageLayout from 'components/common/PageLayout';
import { setDefaultCompanyContext, hasCompanyContext, getCompanyContext } from 'utils/setCompanyContext';
import { normalizeCompanyName } from 'utils/companyNormalizer';
import { DateRangeFilterBlur } from 'components/filters';
import {
  KPISection,
  ReceitaCidadeSection,
  AnaliseCidadeSection,
  ContasPagamentoSection,
  FluxoCaixaSection,
  RecebimentosSection,
  ParcelamentosMovfit
} from 'features/financeiro/components/financeiro';
import FinancialErrorHandler from 'features/financeiro/components/FinancialErrorHandler';

const DashboardFinanceiro = () => {
  // Definir contexto da empresa se não existir
  useEffect(() => {
    if (!hasCompanyContext()) {
      setDefaultCompanyContext();
    }
  }, []);

  // Estado para o filtro de data
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [range, setRange] = useState({ presetId: 'mtd', start: toISO(startOfMonth), end: toISO(today) });
  const [tipoFinanceiro, setTipoFinanceiro] = useState('todos');

  // Função para formatar período selecionado
  const formatarPeriodo = (range) => {
    if (!range || !range.start || !range.end) {
      return 'Período não selecionado';
    }

    const formatarData = (dataStr) => {
      const [ano, mes, dia] = dataStr.split('-');
      const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return `${dia}/${meses[parseInt(mes) - 1]}/${ano}`;
    };

    const dataInicio = formatarData(range.start);
    const dataFim = formatarData(range.end);

    // Se for o mesmo dia
    if (range.start === range.end) {
      return dataInicio;
    }

    return `${dataInicio} a ${dataFim}`;
  };

  const {
    dadosFinanceiros,
    loading,
    initialLoading,
    error,
    atualizarDados
  } = useFinanceiro();

  // Processar dados financeiros
  const {
    dadosReais,
    prepararDadosPareto,
    dadosAnaliseCidade,
    dadosContasPagamento,
    dadosFluxoCaixaHistoricoGrafico
  } = useFinanceiroData(dadosFinanceiros, tipoFinanceiro);

  const handleRefresh = () => {
    if (atualizarDados) {
      atualizarDados(range);
    }
  };

  const empresaAtual = getCompanyContext()?.companies?.[0]?.name || '';
  const isMovFit = normalizeCompanyName(empresaAtual) === 'mov fit';

  // Carregar dados quando o range mudar (evitar primeira chamada pois o hook já carrega na montagem)
  const [isInitialMount, setIsInitialMount] = useState(true);
  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }
    if (atualizarDados) {
      atualizarDados(range);
    }
  }, [range.start, range.end]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading inicial da página
  if (initialLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <PageLayout showRefreshButton customRefreshFunction={handleRefresh}>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <FinancialErrorHandler 
            error={error}
            onRetry={handleRefresh}
            loading={loading}
            title="Erro ao carregar dados financeiros"
          />

          {/* Mensagem amigável ao usuário */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Serviço temporariamente indisponível
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Os dados financeiros para esta empresa não estão disponíveis no momento. 
                    Isso pode acontecer quando a rota N8N específica ainda não está configurada.
                  </p>
                  <p className="mt-2">
                    <strong>Dica:</strong> Tente trocar para outra empresa ou volte mais tarde.
                  </p>
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={handleRefresh}
                    className="bg-yellow-100 text-yellow-800 px-3 py-2 rounded-md text-sm font-medium hover:bg-yellow-200 transition-colors"
                  >
                    Tentar novamente
                  </button>
                  <button
                    onClick={() => window.history.back()}
                    className="bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] px-3 py-2 rounded-md text-sm font-medium hover:bg-[color:var(--surface)] transition-colors"
                  >
                    Voltar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Removido LoadingSpinner para permitir blur do CompanyChangeBlur

  return (
    <>
      <CompanyChangeBlur />
      <PageLayout showRefreshButton customRefreshFunction={handleRefresh}>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4 pb-8 lg:pb-4">
        {/* Seção de filtros por data */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">Dashboard Financeiro</h2>
              {loading ? (
                <div className="flex items-center text-sm text-[color:var(--accent)]">
                  <svg className="h-4 w-4 mr-1 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                  Atualizando...
                </div>
              ) : null}
            </div>
            <DateRangeFilterBlur
              value={range}
              loading={loading}
              onChange={(r) => {
                setRange(r);
              }}
            />
          </div>
          {/* Indicador de período selecionado */}
          <div className="mt-3 flex items-center gap-2">
            <svg className="h-4 w-4 text-[color:var(--text-muted)] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm text-[color:var(--text-muted)]">
              <span className="font-medium text-[color:var(--text-primary)]">Período:</span> {formatarPeriodo(range)}
            </span>
          </div>
        </div>

        {/* Seção de KPIs */}
        <div className="mb-6">
          <KPISection dadosReais={dadosReais} />
        </div>

        {/* Layout: Gráfico e Tabela lado a lado */}
        <div className="mb-8 lg:mb-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">Cidades</h3>
              <p className="text-sm text-[color:var(--text-muted)]">
                Pareto e análise detalhada por tipo financeiro
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:min-w-[280px]">
              <label htmlFor="tipo-financeiro-filter" className="text-sm font-medium text-[color:var(--text-primary)]">
                Tipo financeiro
              </label>
              <select
                id="tipo-financeiro-filter"
                value={tipoFinanceiro}
                onChange={(e) => setTipoFinanceiro(e.target.value)}
                className="w-full rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--text-primary)] shadow-sm outline-none transition-colors focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/20"
              >
                <option value="todos">Todos</option>
                <option value="avulso">Avulso</option>
                <option value="recorrente">Recorrente</option>
                <option value="sem_contrato">Sem contrato</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 sm:h-96">
              <ReceitaCidadeSection prepararDadosPareto={prepararDadosPareto} />
            </div>
            <div className="h-80 sm:h-96">
              <AnaliseCidadeSection dadosAnaliseCidade={dadosAnaliseCidade} />
            </div>
          </div>
        </div>

        {/* Layout: Contas a pagar e Fluxo de Caixa */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-6 mb-16 sm:mb-12 lg:mb-8">
          <div className="order-1 lg:order-1">
            <ContasPagamentoSection dadosContasPagamento={dadosContasPagamento} />
          </div>
          <div className="order-2 lg:order-2">
            <FluxoCaixaSection dadosFluxoCaixaHistoricoGrafico={dadosFluxoCaixaHistoricoGrafico} />
          </div>
        </div>

        {/* Recebimentos - nova subaba com 3 visões */}
        <div className="mb-8">
          <RecebimentosSection dataRange={range} />
        </div>

        {isMovFit && (
          <div className="mb-8">
            <ParcelamentosMovfit empresaAtual={empresaAtual} />
          </div>
        )}

        {/* Separador visual para mobile */}
        <div className="block sm:hidden border-t border-[color:var(--border)] my-12"></div>
        </div>
      </PageLayout>
    </>
  );
};

export default DashboardFinanceiro;
